/**
 * S6 — qué días de una plantilla no dejan enteras sus sesiones, y la frase que
 * lo dice. El reparto es `layoutDay` (@dosfilos/domain), la misma regla del
 * generador: lo que aquí se avisa es exactamente lo que el syllabus va a tener.
 *
 * Lo usan el editor de plantillas (un aviso por día y módulo) y el diálogo que
 * aparece al guardar.
 */
import { dayFitSummary, layoutDay, type DayFitSummary, type LayoutSegment } from '@dosfilos/domain';
import type { TFunction } from 'i18next';

export interface FitModule {
  name: string;
  dayOffsets: { weekday: string; weekIndex: number }[];
  sessions: { dayIndex: number; durationMinutes?: number | undefined }[];
}

export interface FitWeekdayTemplate {
  weekday: string;
  segments: LayoutSegment[];
}

export interface TemplateFitIssue {
  moduleIndex: number;
  moduleName: string;
  dayIndex: number;
  weekday: string;
  weekIndex: number;
  summary: DayFitSummary;
}

/** Los días (de cada módulo) cuyas sesiones se recortan o no caben. */
export function templateFitIssues(
  modules: readonly FitModule[],
  weekdayTemplates: readonly FitWeekdayTemplate[],
): TemplateFitIssue[] {
  const out: TemplateFitIssue[] = [];
  modules.forEach((m, moduleIndex) => {
    m.dayOffsets.forEach((day, dayIndex) => {
      const wk = weekdayTemplates.find((w) => w.weekday === day.weekday);
      // Sin formato de jornada para ese día, el editor ya lo avisa aparte.
      if (!wk) return;
      const summary = dayFitSummary(
        layoutDay(
          wk.segments,
          m.sessions.filter((s) => s.dayIndex === dayIndex),
        ),
      );
      if (summary) {
        out.push({
          moduleIndex,
          moduleName: m.name,
          dayIndex,
          weekday: day.weekday,
          weekIndex: day.weekIndex,
          summary,
        });
      }
    });
  });
  return out;
}

/** «10, 15 y 20» / «10, 15 and 20». */
const listOf = (t: TFunction, items: string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} ${t('templateEditor.modules.fit.and')} ${items[items.length - 1]}`;

/** «7 sesiones de 45 min = 315 min; la jornada del sábado tiene 230 min de
 *  clase: 3 sesiones quedarían recortadas (10, 15 y 20 min) y sobran 5 min». */
export function fitSentence(t: TFunction, s: DayFitSummary, weekday: string): string {
  const k = 'templateEditor.modules.fit';
  const sessions =
    s.eachMinutes !== null
      ? t(`${k}.sessionsEach`, {
          count: s.sessions,
          each: s.eachMinutes,
          total: s.requestedMinutes,
        })
      : t(`${k}.sessionsMixed`, { count: s.sessions, total: s.requestedMinutes });
  const parts: string[] = [];
  if (s.truncatedTo.length > 0) {
    parts.push(
      t(`${k}.truncated`, {
        count: s.truncatedTo.length,
        list: listOf(
          t,
          s.truncatedTo.map((m) => String(m)),
        ),
      }),
    );
  }
  if (s.belowMinimum > 0) parts.push(t(`${k}.belowMinimum`, { count: s.belowMinimum }));
  if (s.unplaced > 0) parts.push(t(`${k}.unplaced`, { count: s.unplaced }));
  if (s.unusedMinutes > 0) parts.push(t(`${k}.unused`, { minutes: s.unusedMinutes }));
  return t(`${k}.sentence`, {
    sessions,
    day: t(`${k}.day`, {
      weekday: t(`${k}.weekday.${weekday}`),
      minutes: s.classMinutes,
    }),
    parts: listOf(t, parts),
  });
}
